import {
    Element, ElementContent, Properties, Root as HastRoot, Text,
} from "hast";
import {visit} from "unist-util-visit";
import {MediaType} from "../enums/enums";

interface VideoOptions {
    width?: string
    height?: string
}

interface DefaultMediaProperties {
    controls: string
    style: string
    controlsList: string
    onerror?: any
}

const allowedVideoProps: string[] = ["width", "height"];

// matches the end of string => .mp4
const videoExtensionRegex: RegExp = new RegExp("(.mp4)$");
// matches the end of string => .mp3 || .wav
const audioExtensionRegex: RegExp = new RegExp("(.mp3|.wav)$");

// default props for video and audio HTML tags
const defaultMediaProperties: DefaultMediaProperties = {
    controls: "controls",
    style: "width: 500px;",
    controlsList: "nodownload", // does not allow media (audio/video) to be downloaded
    onerror: undefined,
};

/**
 * Changes the img node to audio/video node => img tag to audio/video tag
 * we provide which of these 2 according to matching regex
 * if options (width or height) are passed we add them as well
 *
 * @param {Element} node
 * @param {MediaType} mediaType
 * @param {VideoOptions} options
 * @return {void}
 */
function imgToOtherMedia(node: Element, mediaType: MediaType, options?: VideoOptions): void {
    // eslint-disable-next-line no-param-reassign
    node.tagName = mediaType;
    // eslint-disable-next-line no-param-reassign
    node.properties = {...node.properties, ...defaultMediaProperties};

    if (options) {
        let optionsString: string = "";

        // eslint-disable-next-line no-restricted-syntax
        for (const [prop, value] of Object.entries(options)) {
            if (allowedVideoProps.includes(prop) && typeof value === "number") {
                optionsString += `${prop}:${value}px;`;
            }
        }
        // eslint-disable-next-line no-param-reassign
        node.properties.style = optionsString;
    }
}

/**
 * Checks the path/ url extension on src property to determine if
 * node should be changed from img to video
 *
 * @param {Element} node
 * @return boolean
 */
function shouldNodeBeVideo(node: Element): boolean {
    const {src}: Properties = node.properties!;
    if (typeof src === "string") {
        return videoExtensionRegex.test(src);
    }
    return false;
}

/**
 * Checks the path/ url extension on src property to determine if
 * node should be changed from img to audio
 *
 * @param {Element} node
 * @return boolean
 */
function shouldNodeBeAudio(node: Element): boolean {
    const {src}: Properties = node.properties!;
    if (typeof src === "string") {
        return audioExtensionRegex.test(src);
    }
    return false;
}

/**
 * Splits the string from node value that comes after the video tag
 *
 * @param {Text} node
 * @return {[string, string]}
 */
function getVideoOptions(node: Text): string[] {
    const splitNodeValue: string[] = node.value.split(/(})/);
    const options: string = [splitNodeValue[0], splitNodeValue[1]].join("");
    return [options, splitNodeValue[2] ?? ""];
}

/**
 * We pass the signResource to onError property to dispatch the action
 * which calls the saga to sign the resource
 *
 * @param {Element} node
 * @param {Function} signResource
 * @param {string} projectType
 * @return {void}
 */
function signMediaResource(node: Element, signResource: Function, projectType: string): void {
    // @ts-ignore
    // eslint-disable-next-line no-param-reassign
    node.properties.onerror = signResource;
    // @ts-ignore
    // eslint-disable-next-line no-param-reassign
    node.properties.projecttype = projectType;
}

// Options in [] for Plugin
/**
 * Plugin that changes the img node to audio or video node based on the path/ url extension
 * optionally, we can add width and height for videos in JSON format
 *
 * @param {Function} signResource
 * @param {string} projectType
 * @return {Function}
 */
const rehypeMedia = (signResource: Function, projectType: string) => (tree: HastRoot): void => {
    visit(
        tree,
        "element",
        (node: Element): void => {
            if (node.tagName === "p") {
                const {children}: Element = node;
                const containsImgNode: boolean = children.some((childNode: ElementContent): boolean => {
                    if (childNode.type === "element") {
                        return childNode.tagName === "img";
                    }
                    return false;
                });
                if (containsImgNode) {
                    children.forEach((currChildNode: ElementContent, index: number): void => {
                        if (currChildNode.type === "element" && currChildNode.tagName === "img") {
                            if (shouldNodeBeVideo(currChildNode)) {
                                imgToOtherMedia(currChildNode, MediaType.VIDEO);
                                const nextChildNode: ElementContent = children[index + 1];
                                if (nextChildNode && nextChildNode.type === "text") {
                                    const [possibleVideoOptions, restOfText]: string[] = getVideoOptions(nextChildNode);
                                    try {
                                        const videoOptions: VideoOptions = JSON.parse(possibleVideoOptions);
                                        imgToOtherMedia(currChildNode, MediaType.VIDEO, videoOptions);
                                        nextChildNode.value = restOfText;
                                    } catch (error) {
                                    // If string can't be parsed into JS object we don't handle the error
                                    // consequently we don't pass options to imgToVideo()
                                    }
                                }
                            }
                            if (shouldNodeBeAudio(currChildNode)) {
                                imgToOtherMedia(currChildNode, MediaType.AUDIO);
                            }
                            signMediaResource(currChildNode, signResource, projectType);
                        }
                    });
                }
            }
        },
    );
};

export default rehypeMedia;
